import { OrganizationAttachmentControllerApiFactory } from '@api-client/api'
import { createFileFromBlobWithHeaders } from '@helpers/fileHelpers'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { useAsyncApi } from './useAsyncApi'

interface CachedAttachment {
	blob: Blob
	headers: Record<string, string | undefined>
	// A Date in ISO string format.
	downloadedAt: string
}

/**
 * Downloads a file with the correct filename.
 *
 * We force download instead of opening in a new tab because:
 * - We use Bearer token authentication, so we can't open backend URLs directly
 * - Blob URLs always show UUIDs in the browser address bar and "Save As" dialog
 * - Forcing download ensures users get files with the correct filename immediately
 *
 * Note: pageNumber parameter is kept for potential future use but currently unused
 * since we download the file instead of opening it in a viewer.
 */
const downloadFileWithFilename = (
	data: File,
	pageNumber?: number // eslint-disable-line @typescript-eslint/no-unused-vars
) => {
	const fileURL = URL.createObjectURL(data)

	const downloadLink = document.createElement('a')
	downloadLink.href = fileURL
	downloadLink.download = data.name
	downloadLink.style.display = 'none'
	document.body.append(downloadLink)
	downloadLink.click()
	document.body.removeChild(downloadLink)

	setTimeout(() => {
		URL.revokeObjectURL(fileURL)
	}, 100)
}

/**
 * Opens a file in a new tab.
 *
 * This is useful for PDF files that users want to view instead of download.
 * Uses Blob URLs which are created from authenticated API responses.
 *
 * @param data - The File object to open
 * @param pageNumber - Optional page number to navigate to (appended as #page={pageNumber} to the URL)
 */
const openFileInNewTab = (data: File, pageNumber?: number) => {
	let fileURL = URL.createObjectURL(data)

	// Add page number fragment if provided
	if (pageNumber !== undefined) {
		fileURL = `${fileURL}#page=${pageNumber}`
	}

	window.open(fileURL, '_blank')

	// Clean up after a delay to ensure the new tab has the URL
	setTimeout(() => {
		URL.revokeObjectURL(fileURL)
	}, 1000)
}

interface UseDownloadAttachmentReturn {
	isLoading: boolean
	downloadAttachment: (
		attachmentId: string,
		pageNumber?: number
	) => Promise<void>
	openAttachmentInNewTab: (
		attachmentId: string,
		pageNumber?: number
	) => Promise<void>
}

const _CACHE_EXPIRATION_TIME_MILLISEC = 864_000_000 // 24 hours

/**
 * Hook for downloading and opening attachments
 * Handles authentication and provides loading state
 * Caches blobs in React Query to avoid redundant requests
 */
export const useDownloadAttachment = (): UseDownloadAttachmentReturn => {
	const attachmentApi = useAsyncApi(OrganizationAttachmentControllerApiFactory)
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)

	const fetchAttachment = useCallback(
		async (attachmentId: string) => {
			const cachedResult = queryClient.getQueryData<CachedAttachment>([
				'attachment',
				attachmentId,
			])
			const validCachedResult =
				cachedResult &&
				new Date(cachedResult.downloadedAt).getTime() +
					_CACHE_EXPIRATION_TIME_MILLISEC >
					Date.now()
					? cachedResult
					: undefined
			const getAttachment = async (): Promise<CachedAttachment> => {
				const response = await attachmentApi(api =>
					api.downloadAttachment1(attachmentId, {
						responseType: 'blob',
					})
				)
				return {
					blob: response.data as Blob,
					headers: response.headers as Record<string, string | undefined>,
					downloadedAt: new Date().toISOString(),
				}
			}
			const result = validCachedResult ?? (await getAttachment())
			if (result && !cachedResult) {
				queryClient.setQueryData(['attachment', attachmentId], result)
			}
			return result
		},
		[queryClient, attachmentApi]
	)

	const downloadAttachment = async (
		attachmentId: string,
		pageNumber?: number
	) => {
		try {
			setIsLoading(true)
			const attachment = await fetchAttachment(attachmentId)
			const file = createFileFromBlobWithHeaders(
				attachment.blob,
				attachment.headers
			)
			downloadFileWithFilename(file, pageNumber)
		} catch {
			// Error handling is implicit - button remains enabled for retry
			throw new Error('Failed to download attachment')
		} finally {
			setIsLoading(false)
		}
	}

	const openAttachmentInNewTab = async (
		attachmentId: string,
		pageNumber?: number
	) => {
		try {
			setIsLoading(true)
			const attachment = await fetchAttachment(attachmentId)
			const file = createFileFromBlobWithHeaders(
				attachment.blob,
				attachment.headers
			)
			openFileInNewTab(file, pageNumber)
		} catch {
			// Error handling is implicit - button remains enabled for retry
			throw new Error('Failed to open attachment')
		} finally {
			setIsLoading(false)
		}
	}

	return {
		isLoading,
		downloadAttachment,
		openAttachmentInNewTab,
	}
}
